import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Crown, Shield, Users, UserPlus, ChevronDown, Trash2, Check, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { validateEmail } from "@/lib/emailValidation";
import { formatCurrency, Currency } from "@/lib/currency";

interface Participant {
  id: string;
  email: string;
  name: string | null;
  status: string;
  role: string;
  has_paid?: boolean;
  contribution_amount?: number | null;
  paid_at?: string | null;
}

interface DiscussionPeopleProps {
  entityType: "event" | "purchase";
  entityId: string;
  userEmail: string;
  userName: string;
  organizerEmail: string;
  organizerName?: string | null;
  participants: Participant[];
  isOrganizer: boolean;
  isAdmin: boolean;
  currency: string;
  targetAmount?: number | null;
  splitType?: string;
  themeColor: string;
  hasPayments?: boolean; // Only show payment status when payments are enabled
  onRefresh: () => void;
}

export function DiscussionPeople({
  entityType,
  entityId,
  userEmail,
  userName,
  organizerEmail,
  organizerName,
  participants,
  isOrganizer,
  isAdmin,
  currency,
  targetAmount,
  splitType,
  themeColor,
  hasPayments = false,
  onRefresh,
}: DiscussionPeopleProps) {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);

  const tableName = entityType === "event" ? "event_invitees" : "group_purchase_participants";
  const foreignKey = entityType === "event" ? "reminder_id" : "purchase_id";

  const activeParticipants = participants.filter(p => p.status === 'active');
  const pastParticipants = participants.filter(p => p.status === 'opted_out' || p.status === 'removed');

  const handleInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    
    const validation = validateEmail(email);
    if (!validation.valid) {
      toast.error(validation.error || "Invalid email");
      return;
    }

    setInviting(true);
    try {
      let error: any = null;
      
      if (entityType === "event") {
        const result = await supabase
          .from("event_invitees")
          .insert({
            reminder_id: entityId,
            email,
            name: inviteName.trim() || null,
            invited_by: userEmail,
          });
        error = result.error;
      } else {
        const result = await supabase
          .from("group_purchase_participants")
          .insert({
            purchase_id: entityId,
            email,
            name: inviteName.trim() || null,
            invited_by: userEmail,
          });
        error = result.error;
      }

      if (error) {
        if (error.code === '23505') {
          toast.error("This person is already invited");
        } else {
          throw error;
        }
        return;
      }

      toast.success("Invitation sent!");
      setInviteEmail("");
      setInviteName("");
      setShowInviteForm(false);
      onRefresh();
    } catch (error) {
      console.error("Error inviting:", error);
      toast.error("Failed to invite");
    } finally {
      setInviting(false);
    }
  };

  const handlePromoteToAdmin = async (participantId: string, email: string) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ role: 'admin' })
        .eq("id", participantId);

      if (error) throw error;
      toast.success(`${email.split('@')[0]} is now an admin`);
      onRefresh();
    } catch (error) {
      console.error("Error promoting:", error);
      toast.error("Failed to promote");
    }
  };

  const handleDemoteToParticipant = async (participantId: string) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ role: 'participant' })
        .eq("id", participantId);

      if (error) throw error;
      toast.success("Role updated");
      onRefresh();
    } catch (error) {
      console.error("Error demoting:", error);
      toast.error("Failed to update role");
    }
  };

  const handleRemove = async (participantId: string, email: string, name?: string | null) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ status: 'removed' })
        .eq("id", participantId);

      if (error) throw error;
      toast.success(`${name || email.split('@')[0]} has been removed`);
      onRefresh();
    } catch (error) {
      console.error("Error removing:", error);
      toast.error("Failed to remove");
    }
  };

  const handleReinvite = async (participantId: string, name?: string | null) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ status: 'active' })
        .eq("id", participantId);

      if (error) throw error;
      toast.success(`${name || 'Participant'} has been re-invited`);
      onRefresh();
    } catch (error) {
      console.error("Error re-inviting:", error);
      toast.error("Failed to re-invite");
    }
  };

  const getShareAmount = () => {
    if (splitType === 'equal' && targetAmount) {
      return targetAmount / (activeParticipants.length + 1);
    }
    return null;
  };

  const shareAmount = getShareAmount();

  const getThemeClasses = () => {
    switch (themeColor) {
      case "pink":
        return {
          button: "bg-pink-600 hover:bg-pink-700",
          accent: "text-pink-600",
          border: "border-pink-200 dark:border-pink-900/30",
          bg: "bg-pink-500/5",
          badge: "bg-pink-500/10 text-pink-700 border-pink-500/20",
        };
      case "emerald":
        return {
          button: "bg-emerald-600 hover:bg-emerald-700",
          accent: "text-emerald-600",
          border: "border-emerald-200 dark:border-emerald-900/30",
          bg: "bg-emerald-500/5",
          badge: "bg-emerald-500/10 text-emerald-700 border-emerald-500/20",
        };
      default:
        return {
          button: "bg-purple-600 hover:bg-purple-700",
          accent: "text-purple-600",
          border: "border-purple-200 dark:border-purple-900/30",
          bg: "bg-purple-500/5",
          badge: "bg-purple-500/10 text-purple-700 border-purple-500/20",
        };
    }
  };

  const theme = getThemeClasses();

  return (
    <div className="space-y-4">
      {/* Header with Invite Button */}
      <div className="flex justify-between items-center">
        <h3 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
          <Users className="w-4 h-4" />
          {activeParticipants.length + 1} Participants
        </h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowInviteForm(!showInviteForm)}
          className="gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Invite
        </Button>
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <Card className={theme.border}>
          <CardContent className="p-4 space-y-3">
            <Input
              type="email"
              placeholder="Email address *"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
            <Input
              placeholder="Name (optional)"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleInvite} disabled={inviting} className={theme.button}>
                {inviting ? "Inviting..." : "Send Invite"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowInviteForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Participants List */}
      <ScrollArea className="h-[320px]">
        <div className="space-y-2">
          {/* Organizer */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${theme.bg} ${theme.border} border`}>
            <Avatar>
              <AvatarFallback className={theme.badge}>
                {(organizerName || organizerEmail)[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">
                {organizerName || organizerEmail.split('@')[0]}
              </p>
              <p className="text-xs text-muted-foreground truncate">{organizerEmail}</p>
            </div>
            {/* Share Amount for Organizer */}
            {hasPayments && shareAmount && (
              <span className="text-sm text-muted-foreground">
                {formatCurrency(shareAmount, currency as Currency)}
              </span>
            )}
            <Badge className={`${theme.badge} gap-1`}>
              <Crown className="w-3 h-3" />
              Organizer
            </Badge>
          </div>

          {/* Active Participants */}
          {activeParticipants.map((p) => {
            const isParticipantAdmin = p.role === 'admin';
            const canManage = (isOrganizer || isAdmin) && p.email.toLowerCase() !== userEmail.toLowerCase();
            
            return (
              <div 
                key={p.id} 
                className={`flex items-center gap-3 p-3 rounded-lg border ${
                  hasPayments && p.has_paid 
                    ? `${theme.bg} ${theme.border}` 
                    : 'bg-muted/30 border-border'
                }`}
              >
                <Avatar>
                  <AvatarFallback className={hasPayments && p.has_paid ? theme.badge : 'bg-muted'}>
                    {(p.name || p.email)[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{p.name || p.email.split('@')[0]}</p>
                    {p.role === 'organizer' && (
                      <Badge className={`${theme.badge} gap-1 text-xs`}>
                        <Crown className="w-3 h-3" />
                        Organizer
                      </Badge>
                    )}
                    {isParticipantAdmin && (
                      <Badge variant="outline" className={`${theme.accent} border-current text-xs`}>
                        <Shield className="w-3 h-3 mr-1" />
                        Admin
                      </Badge>
                    )}
                    {p.role === 'participant' && !isParticipantAdmin && (
                      <Badge variant="outline" className="text-muted-foreground text-xs">
                        Participant
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                </div>
                
                {/* Share Amount */}
                {shareAmount && (
                  <span className="text-sm text-muted-foreground">
                    {formatCurrency(shareAmount, currency as Currency)}
                  </span>
                )}
                
                {/* Payment Status - only when payments are enabled */}
                {hasPayments && (
                  p.has_paid ? (
                    <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                      <Check className="w-3 h-3 mr-1" />
                      Paid
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                      <Clock className="w-3 h-3 mr-1" />
                      Pending
                    </Badge>
                  )
                )}
                
                {/* Management Dropdown */}
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <ChevronDown className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {isOrganizer && (
                        <>
                          {isParticipantAdmin ? (
                            <DropdownMenuItem onClick={() => handleDemoteToParticipant(p.id)}>
                              <Users className="w-4 h-4 mr-2" />
                              Remove admin
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handlePromoteToAdmin(p.id, p.email)}>
                              <Shield className="w-4 h-4 mr-2" />
                              Make admin
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                        </>
                      )}
                      <DropdownMenuItem 
                        onClick={() => handleRemove(p.id, p.email, p.name)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </div>

        {/* Past Participants */}
        {pastParticipants.length > 0 && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-sm font-medium text-muted-foreground mb-2">
              Past Participants ({pastParticipants.length})
            </h4>
            <div className="space-y-2">
              {pastParticipants.map((p) => (
                <div key={p.id} className="flex items-center gap-3 p-2 rounded-lg opacity-60 hover:opacity-100 transition-opacity">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-muted text-xs">
                      {(p.name || p.email)[0].toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name || p.email.split('@')[0]}</p>
                    <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                  </div>
                  <Badge variant="outline" className="text-muted-foreground text-xs">
                    {p.status === 'removed' ? 'Removed' : 'Left'}
                  </Badge>
                  {(isOrganizer || isAdmin) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className={`h-7 ${theme.accent} hover:${theme.bg}`}
                      onClick={() => handleReinvite(p.id, p.name)}
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Re-invite
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
